/*!
 * Copyright © 2026. Verizon Connect Ireland Limited. All rights reserved.
 */

import { forkJoin, Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import {
  EPlotType,
  IAssetPlot,
  IAttributeListItem,
  IEntityMappingEntry,
  IMapToUuidRequest,
  IMapToUuidResponse,
  ISearchAssignmentItem,
  ISearchAssignmentsAttributeFilterItem,
  ISearchAssignmentsEntityFilter,
  ISearchAssignmentsRequest,
  ISearchAssignmentsResponse,
  IVehiclePlot
} from '../models';

export type SearchAssignmentsFn = (body: ISearchAssignmentsRequest) => Observable<ISearchAssignmentsResponse>;

/**
 * Three-state filter result:
 * - `null` → no filter active (show everything)
 * - empty Set → filter active but no matches (show nothing)
 * - Set with values → filter active with matches (show only those)
 */
export type FilteredEntityIds = Set<string> | null;

export interface EntityIdGroup<TId = string> {
  type: string;
  ids: TId[];
}

export const MAX_ENTITY_IDS_PER_REQUEST = 1000;
export const MAX_ITEMS_PER_PAGE = 1000;
export const MAX_PAGINATION_DEPTH = 50;

export interface PaginatedResponse<T> {
  items: T[];
  totalCount: number | null;
}

export type PaginatedFetchFn<T> = (pagination: {
  limit: number;
  offset: number;
  totalRequired: boolean;
}) => Observable<PaginatedResponse<T>>;

/**
 * Generic paginated fetch: recursively fetches all pages of a paginated API response.
 * First page uses totalRequired: true to get the total count,
 * subsequent pages use the saved totalCount to determine if more pages exist.
 */
export function fetchAllPages<T>(fetchFn: PaginatedFetchFn<T>, limit = MAX_ITEMS_PER_PAGE): Observable<T[]> {
  return fetchPagesRecursive(fetchFn, { limit, offset: 0, totalRequired: true });
}

/**
 * Searches all assignments matching the given attribute filters and entities.
 * Handles double pagination: splits entity IDs into batches of max 1000 per request,
 * and fetches all response pages for each batch. Batches run in parallel via forkJoin.
 */
export function searchAllAssignments(
  searchFn: SearchAssignmentsFn,
  attributeFilters: ISearchAssignmentsAttributeFilterItem[],
  entityGroups: EntityIdGroup[]
): Observable<ISearchAssignmentItem[]> {
  const batches = batchEntityIds(entityGroups);

  if (batches.length === 0) {
    return of([]);
  }

  const requests$ = batches.map(batchEntities =>
    fetchAllPages<ISearchAssignmentItem>(pagination =>
      searchFn({
        attributesFilter: { attributes: attributeFilters },
        entities: batchEntities,
        pagination
      })
    )
  );

  return forkJoin(requests$).pipe(
    map(results => {
      const merged: ISearchAssignmentItem[] = [];
      for (const items of results) {
        merged.push(...items);
      }
      return merged;
    })
  );
}

/**
 * Flattens all entity IDs across types and splits them into batches
 * of max `batchSize` total IDs, regrouped by type via `toGroup`.
 */
function batchByType<TId, TGroup>(
  entityGroups: EntityIdGroup<TId>[],
  batchSize: number,
  toGroup: (type: string, ids: TId[]) => TGroup
): TGroup[][] {
  const allEntries: { type: string; id: TId }[] = [];
  for (const group of entityGroups) {
    for (const id of group.ids) {
      allEntries.push({ type: group.type, id });
    }
  }

  const batches: TGroup[][] = [];
  for (let i = 0; i < allEntries.length; i += batchSize) {
    const batchEntries = allEntries.slice(i, i + batchSize);
    const grouped = new Map<string, TId[]>();
    for (const entry of batchEntries) {
      if (!grouped.has(entry.type)) {
        grouped.set(entry.type, []);
      }
      grouped.get(entry.type).push(entry.id);
    }

    const batch: TGroup[] = [];
    for (const [type, ids] of grouped) {
      batch.push(toGroup(type, ids));
    }
    batches.push(batch);
  }

  return batches;
}

/**
 * Flattens all entity IDs across types and splits them into batches
 * of max MAX_ENTITY_IDS_PER_REQUEST total IDs, regrouped by type.
 */
function batchEntityIds(entityGroups: EntityIdGroup[]): ISearchAssignmentsEntityFilter[][] {
  return batchByType(entityGroups, MAX_ENTITY_IDS_PER_REQUEST, (type, entityIds) => ({ type, entityIds }));
}

function fetchPagesRecursive<T>(
  fetchFn: PaginatedFetchFn<T>,
  pagination: { limit: number; offset: number; totalRequired: boolean },
  knownTotalCount?: number,
  depth = 0
): Observable<T[]> {
  if (depth >= MAX_PAGINATION_DEPTH) {
    return of([]);
  }

  return fetchFn(pagination).pipe(
    mergeMap(response => {
      if (response.items.length === 0) {
        return of([]);
      }

      const totalCount = knownTotalCount ?? response.totalCount;
      const fetchedSoFar = pagination.offset + response.items.length;
      const hasMorePages = totalCount != null && fetchedSoFar < totalCount;

      if (hasMorePages) {
        return fetchPagesRecursive(
          fetchFn,
          // Offset is item-based (total items fetched so far), not page-based
          { ...pagination, offset: fetchedSoFar, totalRequired: false },
          totalCount,
          depth + 1
        ).pipe(map(nextItems => [...response.items, ...nextItems]));
      }

      return of(response.items);
    })
  );
}

/**
 * Builds the composite key used to match entities in the filtered entity ID set.
 */
export function buildEntityCompositeKey(entityType: string, entityId: string | number): string {
  return `${entityType}:${entityId}`;
}

/**
 * Filters items by the three-state filteredEntityIds:
 * - null → no filter active, returns all items
 * - empty Set → filter active but no matches, returns []
 * - Set with values → returns only items whose composite key is in the Set
 */
export function filterByEntityIds<T extends { type: EPlotType; id: number }>(items: T[], filteredEntityIds: FilteredEntityIds): T[] {
  if (filteredEntityIds === null) {
    return items;
  }
  if (filteredEntityIds.size === 0) {
    return [];
  }
  return items.filter(item => filteredEntityIds.has(buildEntityCompositeKey(plotTypeToEntityType(item.type), item.id)));
}

export function buildEntityKey(entityGroups: EntityIdGroup[]): string {
  const keys: string[] = [];
  for (const entity of entityGroups) {
    for (const id of entity.ids) {
      keys.push(`${entity.type}:${id}`);
    }
  }
  keys.sort((a, b) => a.localeCompare(b));
  return keys.join(',');
}

/**
 * Maps an EPlotType to the corresponding API entity type string.
 */
export function plotTypeToEntityType(plotType: EPlotType): string {
  switch (plotType) {
    case EPlotType.vehicle:
      return 'Vehicle';
    case EPlotType.poweredAsset:
      return 'PoweredAsset';
    case EPlotType.asset:
      return 'NonPoweredAsset';
    default:
      return 'Vehicle';
  }
}

/**
 * Converts selected attributes to the attribute filter format expected by the search assignments API.
 */
export function toAttributeFilters(selectedAttributes: IAttributeListItem[]): ISearchAssignmentsAttributeFilterItem[] {
  return selectedAttributes.map(({ attributeId, stringValues }) => ({ attributeId, stringValues }));
}

/**
 * Builds the complete SearchAssignments payload from selected attributes and current plots.
 */
export function buildSearchPayload(
  selectedAttributes: IAttributeListItem[],
  vehiclePlots: IVehiclePlot[],
  assetPlots: IAssetPlot[]
): { attributeFilters: ISearchAssignmentsAttributeFilterItem[]; entityIds: EntityIdGroup[] } {
  return {
    attributeFilters: toAttributeFilters(selectedAttributes),
    entityIds: buildEntityIds(vehiclePlots, assetPlots)
  };
}

/**
 * Maps vehicle/asset plots to entity ID groups expected by the search assignments API.
 * Vehicle plots are split into 'Vehicle' and 'PoweredAsset' based on their EPlotType.
 * Asset plots are mapped to 'NonPoweredAsset'.
 */
export function buildEntityIds(vehiclePlots: IVehiclePlot[], assetPlots: IAssetPlot[]): EntityIdGroup[] {
  const vehicleIds: string[] = [];
  const poweredAssetIds: string[] = [];
  const nonPoweredAssetIds: string[] = [];

  for (const plot of vehiclePlots) {
    if (plot.type === EPlotType.poweredAsset) {
      poweredAssetIds.push(String(plot.id));
    } else {
      vehicleIds.push(String(plot.id));
    }
  }

  for (const plot of assetPlots) {
    nonPoweredAssetIds.push(String(plot.id));
  }

  const entityGroups: EntityIdGroup[] = [];
  if (vehicleIds.length) {
    entityGroups.push({ type: 'Vehicle', ids: vehicleIds });
  }
  if (poweredAssetIds.length) {
    entityGroups.push({ type: 'PoweredAsset', ids: poweredAssetIds });
  }
  if (nonPoweredAssetIds.length) {
    entityGroups.push({ type: 'NonPoweredAsset', ids: nonPoweredAssetIds });
  }

  return entityGroups;
}

/**
 * Returns only the entity IDs not yet present in the existing mapping.
 */
export function buildMappingDiff(entityGroups: EntityIdGroup[], existingMap: Record<string, string>): EntityIdGroup<number>[] {
  return entityGroups
    .map(entity => ({
      type: entity.type,
      ids: entity.ids.filter(id => !(`${entity.type}:${id}` in existingMap)).map(Number)
    }))
    .filter(entity => entity.ids.length > 0);
}

/**
 * Splits entity IDs into batched IMapToUuidRequest payloads of max `batchSize` total IDs.
 */
export function batchMappingRequests(entityGroups: EntityIdGroup<number>[], batchSize: number): IMapToUuidRequest[] {
  const batches = batchByType(entityGroups, batchSize, (entityType, legacyIds) => ({ entityType, legacyIds }));
  return batches.map(batch => ({ items: batch }));
}

/**
 * Flattens grouped mapping API responses into a flat array of entity mapping entries.
 */
export function flattenMappingResponses(responses: IMapToUuidResponse[]): IEntityMappingEntry[] {
  const entries: IEntityMappingEntry[] = [];
  for (const response of responses) {
    for (const item of response.items) {
      for (const m of item.mappings) {
        entries.push({ entityType: item.entityType, legacyId: m.legacyId, uuid: m.uuid });
      }
    }
  }
  return entries;
}

/**
 * Converts legacy entity IDs to UUIDs using the mapping lookup.
 */
export function convertEntityIdsToUuids(entityGroups: EntityIdGroup[], legacyMapToUuid: Record<string, string>): EntityIdGroup[] {
  return entityGroups.map(entity => ({
    type: entity.type,
    ids: entity.ids.map(id => {
      const key = `${entity.type}:${id}`;
      return legacyMapToUuid[key] ?? id;
    })
  }));
}
